import {orderBy} from "lodash"
import moment from "moment-timezone"

export type ProfileCampusTimezoneSource = {
    id?: number
    active?: number | boolean | null
    isDefault?: number | boolean | null
    campuses?: Array<{timezone?: string | null} | null> | null
}

export const isIanaTimezone = (timezone: unknown): timezone is string =>
    typeof timezone === "string" && timezone.length > 0 && !!moment.tz.zone(timezone)

const profileTimezoneRank = (profile: ProfileCampusTimezoneSource): number =>
    (profile.isDefault ? 2 : 0) + (profile.active ? 1 : 0)

/** Campus timezone of a user profile. Empty when the profile has no campus timezone. */
export const getCampusTimezone = (
    profiles: ProfileCampusTimezoneSource[] | null | undefined,
    preferredProfileId?: number | null
): string | null => {
    if (!profiles?.length) {
        return null
    }

    const ordered: ProfileCampusTimezoneSource[] = []
    if (preferredProfileId != null) {
        const preferred = profiles.find((profile) => profile?.id === preferredProfileId)
        if (preferred) {
            ordered.push(preferred)
        }
    }

    const rest = orderBy(
        profiles.filter((profile) => profile && !ordered.includes(profile)),
        profileTimezoneRank,
        "desc"
    )
    ordered.push(...rest)

    for (const profile of ordered) {
        const timezone = profile?.campuses?.find((campus) => isIanaTimezone(campus?.timezone))?.timezone
        if (isIanaTimezone(timezone)) {
            return timezone
        }
    }

    return null
}

/** UTC instant shown on the wall clock of `timezone`. */
export const momentInTimezone = (value: string | Date, timezone?: string | null) => {
    const instant = moment.utc(value)
    return isIanaTimezone(timezone) ? instant.tz(timezone) : instant
}

export const resolvePrimaryTimezone = (
    savedTimeZone: string | null | undefined,
    campusTimeZone: string | null,
    fallback?: string
): string => {
    if (isIanaTimezone(savedTimeZone)) {
        return savedTimeZone
    }
    if (campusTimeZone) {
        return campusTimeZone
    }
    return fallback || moment.tz.guess() || "UTC"
}
